import type { CBClientData } from '@/lib/codebaby/types';
import { PERSONAS_SEED } from '@/seed/personas';
import type { ScenarioConfig } from '@/types/scenario';
import type { TranscriptTurn } from '@/types/session';

/**
 * The coach's paused-for-coaching hint — composed from live session signals so
 * it feels responsive to what the rep is actually doing. Pure function so
 * the hint bank can be iterated without touching the screen, and so the
 * live CodeBaby flow (when we wire it) can use the same heuristics on its
 * side if needed.
 */

export interface HintInputs {
  scenario: ScenarioConfig;
  transcript: TranscriptTurn[];
  clientDataHistory: CBClientData[];
  temperature: number;
}

export interface HintPayload {
  /** The actual hint text shown on the purple hint card (per override #3, text only). */
  text: string;
  /** Small tag rendered above the hint body — categorizes the situation for the rep. */
  tag: string;
}

export function composeHint(inputs: HintInputs): HintPayload {
  const { scenario, transcript, clientDataHistory, temperature } = inputs;
  const personaFirst = scenario.display_name.split(' ')[0]!;
  const { subject, object, possessive } = PERSONAS_SEED[scenario.persona_id].pronouns;
  const Subject = subject[0]!.toUpperCase() + subject.slice(1);
  const turns = transcript.filter((t) => t.speaker === 'learner').length;
  const recent = clientDataHistory[clientDataHistory.length - 1];
  const winsRecent = clientDataHistory.slice(-3).filter((c) => c.moment_type === 'win').length;

  // Pull the actual words from the conversation so the hint quotes what just
  // happened instead of reading like a stock card. `lastLearner` and
  // `lastPersona` may be undefined on the very first turn — branch 1 handles
  // that case explicitly.
  const lastLearner = lastTurn(transcript, 'learner');
  const lastPersona = lastTurn(transcript, 'persona');
  const learnerSnippet = lastLearner ? quoteSnippet(lastLearner.text) : '';
  const personaSnippet = lastPersona ? quoteSnippet(lastPersona.text) : '';
  const personaPivotPhrase = pivotPhrase(lastPersona?.text);

  // 1) Haven't spoken yet / just started — orient, don't pitch.
  if (turns === 0) {
    const opener = personaSnippet
      ? `${personaFirst} just said: "${personaSnippet}" Don't answer that yet. `
      : `${personaFirst} is sizing you up. `;
    return {
      tag: 'Open cold',
      text: `${opener}Before you pitch, ask what a bad Monday looks like for ${possessive} ops team. Hear ${object} before ${subject} hears you.`,
    };
  }

  // 2) Premature close detected on the most recent turn.
  if (recent?.close_size === 'large') {
    const echo = learnerSnippet
      ? `You just said: "${learnerSnippet}" That's a close before you've earned the ask. `
      : `You pushed the close before earning it. `;
    return {
      tag: 'Slow the close',
      text: `${echo}Back off. ${personaFirst} needs to feel heard first — reopen with ${possessive} own words and a specific question.`,
    };
  }

  // 3) Just drifted generic (growth moment on the last turn).
  if (recent?.moment_type === 'growth') {
    const echo = learnerSnippet
      ? `That answer — "${learnerSnippet}" — drifted generic. `
      : `That one drifted generic. `;
    const anchor = personaPivotPhrase
      ? `Pivot back to specifics. Pick up on what ${subject} just said about "${personaPivotPhrase}" and ask for a concrete moment — last Monday, the lunch rush, a number ${subject} can quote.`
      : `${personaFirst} wants specifics about ${possessive} actual operation. Ask for a concrete moment — last week, the rush window, a number ${subject} can quote.`;
    return { tag: 'Get specific', text: `${echo}${anchor}` };
  }

  // 4) Persona is ready — go for the small yes.
  if (temperature >= 3.2) {
    const anchor = personaSnippet
      ? `${Subject} just opened a door with: "${personaSnippet}" `
      : `${Subject}'s ready. `;
    return {
      tag: 'Close the loop',
      text: `${anchor}Walk through it small. A 30-minute scope call, a focused diagnostic, an intro to ${possessive} ops lead. Don't oversize the ask.`,
    };
  }

  // 5) Multiple wins in a row — momentum, don't blow it.
  if (winsRecent >= 2 && temperature >= 2.2) {
    const echo = personaSnippet
      ? `${Subject} just gave you: "${personaSnippet}" `
      : `You've got ${object}. `;
    return {
      tag: 'Keep it tight',
      text: `${echo}Don't pitch now — that's how you lose it. Use ${possessive} last sentence as your next question, or tee up a small next step.`,
    };
  }

  // 6) Stuck cold — persona hasn't budged.
  if (temperature < 1.1) {
    const echo = personaSnippet
      ? `${Subject} just said: "${personaSnippet}" That's still defensive — you haven't found the pressure point. `
      : `${Subject}'s cold. `;
    return {
      tag: 'Warm before pushing',
      text: `${echo}Acknowledge ${possessive} framing first, then ask something narrow. Don't argue. Don't sell. Pull a specific moment out of the abstraction.`,
    };
  }

  // 7) Default — persona is listening, you're making progress, stay patient.
  const echo = personaSnippet
    ? `${personaFirst} just gave you: "${personaSnippet}" That's the seed. `
    : `You're making progress. `;
  return {
    tag: 'Stay specific',
    text: `${echo}Don't move on yet. Echo ${possessive} exact words back as a question and force a number. If ${subject} said "${personaPivotPhrase || 'fine'}," make ${object} define what that costs in real terms.`,
  };
}

/** Most recent turn from a given speaker, if any. */
function lastTurn(transcript: TranscriptTurn[], speaker: 'learner' | 'persona') {
  for (let i = transcript.length - 1; i >= 0; i--) {
    const t = transcript[i];
    if (t && t.speaker === speaker) return t;
  }
  return undefined;
}

/**
 * Trim a turn down to a hint-friendly snippet. Long turns get truncated to
 * the first sentence (or first ~80 chars), so the hint card stays scannable.
 */
function quoteSnippet(text: string): string {
  const t = text.trim();
  if (!t) return '';
  // Prefer first sentence boundary.
  const sentenceMatch = t.match(/^(.{8,120}?[.?!])(\s|$)/);
  if (sentenceMatch) return sentenceMatch[1]!;
  if (t.length <= 80) return t;
  return `${t.slice(0, 78).trimEnd()}…`;
}

/**
 * Pull a short noun-y phrase the persona just used that would be useful to
 * echo back (e.g. "tax we already pay", "ten minutes"). Heuristic: scan for
 * a short quoted phrase or a noun phrase after "the" / "a" / "our". Falls
 * back to undefined when nothing usable is found, and the caller plugs in a
 * generic pivot.
 */
function pivotPhrase(text: string | undefined): string | undefined {
  if (!text) return undefined;
  // Quoted phrases the persona used inside their own reply.
  const quoted = text.match(/['"]([^'"]{3,40})['"]/);
  if (quoted) return quoted[1];
  // Short hot phrases — number + noun, time-anchor, etc.
  const numberish = text.match(
    /\b(\d+\s*(minutes?|seconds?|hours?|days?|weeks?|months?|stores?|locations?|outages?|orders?))\b/i,
  );
  if (numberish) return numberish[1];
  return undefined;
}
